/**
 * ACT-1342 — REST read/write functions for the Quality Gaps checklist,
 * hitting providerPortalAPI directly. Replaces the abandoned
 * care-management-gql GraphQL path (`useConnectQualityGapClosures` /
 * `graphql/qualityGapClosures.ts`, deleted alongside this file) — that
 * backend contract was never shipped and its table is being dropped.
 *
 * Same base-URL convention as every other host-owned PP-API call (see
 * `auth/ppEligibility.ts`'s `checkPatientEligibility` and
 * `worklist/fetchEntitledIpas.ts`): the RAW `VITE_PROVIDER_PORTAL_API_BASE_URL`
 * (no trailing "/api" — callers append it), a BARE token (no "Bearer "
 * scheme prefix) in the `Authorization` header, and a thrown Error on any
 * non-OK response so the caller can distinguish "no data" from "the request
 * failed" rather than collapsing the two.
 *
 * Keyed on `astranaId` (never the Pathways Mongo `_id` the GraphQL path used
 * as `memberObjectId`) — the whole point of the redesign: it works for any
 * member with a resolvable astranaId, including connect-only fallback
 * members that carry no Pathways record at all. See `useQualityGapClosures.ts`
 * for the host-side hook wired to these.
 */

export interface QualityGapClosure {
  submeasureCode: string;
  measureYear: number;
  closed: boolean;
  note: string | null;
  actor: string | null;
  source: "review" | "member_profile";
  reviewId: string | null;
  updatedAt: string | null;
  /**
   * AM-6518 — the reviewer's "Mark status" pick (Pending / Completed /
   * Scheduled-Referred / Could Not Complete / Patient Exclusion), or `null`
   * for a row that has never had one asserted (pre-AM-6518 closures, or a
   * closure written by a caller that never sent one). Server-typed as a bare
   * `string` here (not `@astrana/ui`'s `Resolution` union) since this is the
   * wire contract — the host narrows it when feeding it to the card.
   */
  resolution: string | null;
}

interface GetMemberClosuresResponse {
  closures: QualityGapClosure[];
}

/**
 * Fetches every closure asserted for this member/measureYear.
 *
 * `bearer` is a BARE PP token (no "Bearer " prefix) — matches every other
 * host PP-API call. Rejects on a non-OK response.
 */
export async function fetchQualityGapClosures(
  ppApiBaseUrl: string,
  bearer: string,
  astranaId: string,
  measureYear: number,
): Promise<QualityGapClosure[]> {
  const url = `${ppApiBaseUrl}/api/quality-gaps/member-closures?astranaId=${encodeURIComponent(
    astranaId,
  )}&measureYear=${measureYear}`;
  const res = await fetch(url, {
    method: "GET",
    headers: { Authorization: bearer },
  });

  if (!res.ok) {
    throw new Error(`GetMemberClosures request failed: ${res.status}`);
  }

  const data = (await res.json()) as GetMemberClosuresResponse | null;
  return Array.isArray(data?.closures) ? data.closures : [];
}

export interface SetQualityGapClosureInput {
  astranaId: string;
  measureYear: number;
  submeasureCode: string;
  closed: boolean;
  note?: string;
  /**
   * AM-6518 — the Mark-status pick to persist alongside `closed`.
   * Omit to leave the stored resolution untouched (only valid while it still
   * agrees with `closed` — the pure boolean toggle path relies on this).
   * Pass explicit `null` to clear it back to unset. providerPortalAPI
   * enforces the pairing server-side: `resolution: "Pending"` requires
   * `closed: false`; every other non-null resolution requires `closed: true`
   * (400 otherwise).
   */
  resolution?: string | null;
}

/** Upserts one closure. Rejects on a non-OK response. */
export async function setQualityGapClosure(
  ppApiBaseUrl: string,
  bearer: string,
  input: SetQualityGapClosureInput,
): Promise<QualityGapClosure> {
  const res = await fetch(`${ppApiBaseUrl}/api/quality-gaps/member-closures`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: bearer,
    },
    body: JSON.stringify(input),
  });

  if (!res.ok) {
    throw new Error(`SetMemberClosure request failed: ${res.status}`);
  }

  return (await res.json()) as QualityGapClosure;
}
